/**
 * TopicDetailRoutePage — route-level wrapper that renders one topic in its
 * own browser tab/page.
 *
 * Mounted at `/project/:projectId/content-plan/topic/:topicId` (access
 * controlled by content-plan/config/access, registered in `frontend/src/App.jsx`
 * after the existing `/content-plan` route). Reads:
 *   • `:projectId`, `:topicId` — `useParams`
 *   • `?tab=` — `useSearchParams` (defaults to `overview`)
 *
 * Loads the topic via the new `useContentTopic` real-time hook and forwards
 * it to `TopicDetailPanel` in fullScreen mode, with controlled tab state
 * driven by the URL. On close, calls `navigate(-1)` so the user lands back
 * where they came from (Backlog, Calendar, etc).
 *
 * The page renders inside `Layout` (NOT in the full-screen-bypass branch),
 * so the menu sidebar's `TasksSidebarPanel` singleton remains visible.
 */

import React from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '@frontend/contexts/AuthContext';
import { TopicDetailPanel } from '../TopicDetailPanel';
import type { TabKey } from '../TopicDetailPanel';
import { useContentTopic } from '../../hooks/useContentTopic';
import { CanvasTasksPanel } from '@modules/reactFlow/components/shared/CanvasTasksPanel';
import { canAccessContentPlan, isContentPlanAdminOnly } from '@modules/content-plan/config/access';
import { useProjectCapability } from '@modules/billing-access';

const VALID_TABS: ReadonlyArray<TabKey> = ['overview', 'research', 'templates', 'voice', 'posts'];

const isTabKey = (v: string | null | undefined): v is TabKey =>
  !!v && (VALID_TABS as ReadonlyArray<string>).includes(v);

export const TopicDetailRoutePage: React.FC = () => {
  const { projectId, topicId } = useParams<{ projectId: string; topicId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const { currentUser, isAdmin, adminLoaded } = useAuth() as {
    currentUser: { uid: string } | null;
    isAdmin: boolean;
    adminLoaded: boolean;
  };

  const tabParam = searchParams.get('tab');
  const tab: TabKey = isTabKey(tabParam) ? tabParam : 'overview';
  const authorId = searchParams.get('author');

  const { topic, loading } = useContentTopic(projectId, topicId);
  const contentPlanAccess = useProjectCapability(projectId, 'content_plan.access');

  if (!projectId || !topicId) {
    return <Navigate to="/all-projects" replace />;
  }

  // Wait for AuthContext to settle before deciding. On cold-load (URL pasted
  // into a new tab) `currentUser` resolves first and `isAdmin` lags behind
  // until the Firestore `users/{uid}` listener fires. Without this gate, the
  // !isAdmin redirect would fire DURING that gap and bounce the user to
  // /all-projects. `adminLoaded` flips true once the first snapshot resolves
  // (admin or not), so we trust it.
  if (!currentUser || (isContentPlanAdminOnly() && (!adminLoaded || contentPlanAccess.loading))) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading…</Typography>
      </Box>
    );
  }

  // Now adminLoaded is true — `isAdmin` reflects the real Firestore value.
  if (!canAccessContentPlan({ isAdmin, hasBillingAccess: contentPlanAccess.allowed })) {
    return <Navigate to="/all-projects" replace />;
  }

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <TopicDetailPanel
        open
        fullScreen
        topic={topic}
        projectId={projectId}
        userId={currentUser.uid}
        authorId={authorId ?? (topic as any)?.authorId ?? null}
        tab={tab}
        onTabChange={(next) => {
          // Preserve any other query params the user may have on the URL.
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set('tab', next);
          setSearchParams(nextParams, { replace: false });
        }}
        onClose={() => {
          // Always navigate explicitly to the Backlog tab on the parent
          // content-plan page. `navigate(-1)` would not work on cold-load
          // (e.g. when the topic URL was opened in a new browser tab and
          // there is no relevant history entry to go back to).
          const params = new URLSearchParams({ cpTab: 'backlog' });
          if (authorId) params.set('author', authorId);
          navigate(`/project/${projectId}/content-plan?${params.toString()}`);
        }}
      />
      {/* Background-tasks tray for this topic page. The fullScreen SDialog
          covers the menu-sidebar TasksSidebarPanel singleton, so we mount the
          same panel here at zIndex 1400 (above MUI Dialog's 1300). Reads from
          the same global Zustand store — no extra Firestore listener. */}
      <CanvasTasksPanel projectId={projectId} zIndex={1400} />
    </>
  );
};

export default TopicDetailRoutePage;
